import { Box, Button, ModalProps } from "@chakra-ui/react";
import useTranslate from "hooks/useTranslate";
import TraitTile from "components/TraitTile";
import { SwiperSlide } from "swiper/react/swiper-react";
import { Controller } from "swiper";
import BeforeAfterPreview from "components/BeforeAfterPreview";
import ControlledSwiper from "components/ControlledSwiper";
import useTraits from "hooks/useTraits";
import { useMemo } from "react";
import { getCategoryIndexByName, getTraitCategory } from "utils/traitsUtils";
import useUnstakeTraits from "hooks/useUnstakeTraits";
import { calculateLevel } from "utils/levelUtils";
import SimplePopup from "components/SimplePopup";

type UnstakePopupProps = Omit<ModalProps, "children"> & {
  originalTraits: number[];
  currentTraits: number[];
  traitsToUnstake: number[];
  tokenId: number;
  furColor: number;
  onSuccess: () => void;
};

const UnstakePopup = ({
  tokenId,
  furColor,
  originalTraits,
  currentTraits,
  traitsToUnstake,
  onSuccess,
  ...rest
}: UnstakePopupProps) => {
  const translate = useTranslate();
  const { traitsByTokenId } = useTraits();

  const isMultiple = traitsToUnstake.length > 1;
  const slidesPerView = isMultiple ? 1.35 : 1;

  const categoriesToUnstake = useMemo(
    () => traitsToUnstake.map((tokenId) => getTraitCategory(tokenId)),
    [traitsToUnstake]
  );

  const traitsAfterUnstake = useMemo(
    () =>
      categoriesToUnstake.map(
        (category) => originalTraits[getCategoryIndexByName(category)]
      ),
    [originalTraits, categoriesToUnstake]
  );

  const [unstake, isUnstaking] = useUnstakeTraits(
    tokenId,
    categoriesToUnstake,
    onSuccess
  );

  return (
    <SimplePopup
      title={translate(
        isMultiple ? "unstake:title:multi" : "unstake:title:single",
        { count: traitsToUnstake.length }
      )}
      description={translate("unstake:description")}
      size="md"
      {...rest}
    >
      <Box pt="8">
        <BeforeAfterPreview
          currentTraits={currentTraits}
          changedTraits={traitsAfterUnstake}
          furColor={furColor}
          imagesSize="256"
          bg="dark.400"
        />
      </Box>

      <Box mt="3">
        <ControlledSwiper
          spaceBetween={2}
          threshold={10}
          modules={[Controller]}
          slidesPerView={slidesPerView}
          freeMode
        >
          {traitsToUnstake.map((tokenId) => {
            const trait = traitsByTokenId[tokenId];
            return trait ? (
              <SwiperSlide key={tokenId}>
                <TraitTile
                  tokenId={tokenId}
                  name={traitsByTokenId[tokenId].name}
                  level={calculateLevel(trait.honeyConsumptionRequirement)}
                  furColor={furColor}
                  px="6"
                  w="full"
                  canvasSize="12"
                  bg="dark.600"
                />
              </SwiperSlide>
            ) : null;
          })}
        </ControlledSwiper>
      </Box>

      <Box px="6" pt="6">
        <Button
          w="full"
          colorScheme="secondary"
          isLoading={isUnstaking}
          onClick={() => unstake()}
        >
          {translate("unstake:submit")}
        </Button>
      </Box>
    </SimplePopup>
  );
};

export default UnstakePopup;
